/**
 * Top 20 itens com maior tempo de execução da fila de contratos
 * Identifica processos lentos ou travados
 */

import { useState, Fragment } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Turtle, AlertTriangle, Clock, FileJson, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useContractQueueSlowestItems, ContractQueueSlowestItem } from '@/hooks/useContractQueueMonitor';

/** Linha individual com payload expansível */
function SlowestItemRow({ item, index }: { item: ContractQueueSlowestItem; index: number }) {
  const [isOpen, setIsOpen] = useState(false);
  const isVerySlow = item.processing_time_ms > 60000;
  const hasPayload = item.message && Object.keys(item.message).length > 0;

  const rankIcon = index < 3 ? ['🥇', '🥈', '🥉'][index] : `${index + 1}`;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} asChild>
      <Fragment>
        <TableRow className={cn(isVerySlow ? 'bg-destructive/5' : '', isOpen && 'bg-muted/20')}>
          <TableCell className="font-medium text-center w-[50px]">
            <span className={index < 3 ? 'text-lg' : 'text-muted-foreground'}>{rankIcon}</span>
          </TableCell>
          <TableCell className="font-mono">
            <div className="flex items-center gap-1">
              {isVerySlow && <AlertTriangle className="h-3 w-3 text-destructive" />}
              <span className={isVerySlow ? 'text-destructive font-semibold' : ''}>{item.processing_time_formatted}</span>
            </div>
          </TableCell>
          <TableCell className="font-mono text-xs text-muted-foreground">{item.msg_id}</TableCell>
          <TableCell>
            <Badge variant="outline">{item.status === 'archived' ? 'Arquivado' : item.status === 'processing' ? 'Proc.' : 'Pendente'}</Badge>
          </TableCell>
          <TableCell><Badge variant="outline" className="font-mono">{item.action || '-'}</Badge></TableCell>
          <TableCell className="max-w-[120px] truncate">
            <TooltipProvider><Tooltip><TooltipTrigger asChild><span>{item.tenant_name || 'N/A'}</span></TooltipTrigger>
              <TooltipContent><p>{item.tenant_name}</p></TooltipContent></Tooltip></TooltipProvider>
          </TableCell>
          <TableCell className="text-xs text-muted-foreground max-w-[150px] truncate">{item.contract_title || '-'}</TableCell>
          <TableCell className="text-center">
            <span className={item.read_ct >= 3 ? 'text-destructive font-bold' : ''}>{item.read_ct}/3</span>
          </TableCell>
          <TableCell className="text-muted-foreground text-sm">
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              <span>{formatDistanceToNow(new Date(item.enqueued_at), { addSuffix: true, locale: ptBR })}</span>
            </div>
          </TableCell>
          <TableCell>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </Button>
            </CollapsibleTrigger>
          </TableCell>
        </TableRow>
        <CollapsibleContent asChild>
          <TableRow className="bg-muted/10 hover:bg-muted/10">
            <TableCell colSpan={10} className="p-0">
              <div className="p-4 space-y-2 border-t border-border/50">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <FileJson className="h-4 w-4 text-primary" /> Payload
                </div>
                {hasPayload ? (
                  <pre className="text-xs bg-muted/50 p-3 rounded-md overflow-x-auto text-muted-foreground max-h-[300px] overflow-y-auto">
                    {JSON.stringify(item.message, null, 2)}
                  </pre>
                ) : <p className="text-sm text-muted-foreground">Sem payload</p>}
              </div>
            </TableCell>
          </TableRow>
        </CollapsibleContent>
      </Fragment>
    </Collapsible>
  );
}

export function ContractQueueSlowestItems() {
  const { data: items, isLoading, error } = useContractQueueSlowestItems(20);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Turtle className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-lg">Top 20 - Maior Tempo de Execução</CardTitle>
        </div>
        <CardDescription>Contratos com maior latência (arquivados + em processamento) nas últimas 24h</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex gap-4"><Skeleton className="h-8 w-[50px]" /><Skeleton className="h-8 w-[80px]" /><Skeleton className="h-8 flex-1" /></div>
            ))}
          </div>
        ) : error ? (
          <div className="text-destructive text-sm">Erro ao carregar dados: {error.message}</div>
        ) : !items || items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
            <Turtle className="h-12 w-12 mb-2 opacity-50" />
            <p className="text-sm">Nenhum item encontrado nas últimas 24 horas</p>
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px] text-center">#</TableHead>
                  <TableHead className="w-[80px]">Tempo</TableHead>
                  <TableHead className="w-[60px]">ID</TableHead>
                  <TableHead className="w-[90px]">Status</TableHead>
                  <TableHead className="w-[120px]">Ação</TableHead>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Contrato</TableHead>
                  <TableHead className="w-[70px]">Tent.</TableHead>
                  <TableHead className="w-[100px]">Criado</TableHead>
                  <TableHead className="w-[50px]">Payload</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item, index) => (
                  <SlowestItemRow key={item.msg_id} item={item} index={index} />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
